/**
 * Connectors (Data Sources) Page
 *
 * Unified listing of all connectors organized by category.
 * Each row opens a Sheet for inline configuration.
 */

import { useState, useEffect, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  logConnectorDegradation,
  logConnectorEvent,
  reportConnectorFailure,
} from '@/lib/observability/connectors'
import { PageShell, PageHeader } from '@/components/shared/layout'
import { DataSourceRow } from '@/components/connectors/DataSourceRow'
import { ConnectorCategorySections, ConnectorSection } from '@/components/connectors/ConnectorCategorySections'
import { ConnectorSheet, type SheetConnectorType } from '@/components/connectors/ConnectorSheet'
import { getRevenueConfig } from '@/lib/api/api'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getAllWorkspaces } from '@/lib/api/workspaces'
import type { RevenueSetup, Workspace } from '@/lib/types/workspace'
import type { OAuthConnection } from '@/lib/types/connector'
import {
  initiateMetaOAuth,
  getMetaConnections,
  refreshConnection,
  disconnectMetaConnection,
  initiateGoogleOAuth,
  getGoogleConnections,
  refreshGoogleConnection,
  disconnectGoogleConnection,
  initiateGSCOAuth,
  getGSCConnections,
  disconnectGSCConnection,
  initiateGoogleAdsOAuth,
  getGoogleAdsConnections,
  refreshGoogleAdsConnection,
  disconnectGoogleAdsConnection,
  type MetaConnection,
  type GoogleConnection,
  type GSCConnection,
  type GoogleAdsConnection,
} from '@/lib/api/oauth'

interface DashboardContext {
  propertyId?: string
  currentDomain?: string
  currentSite?: { domain?: string; name?: string }
}

// ── Helpers ──

function toOAuthConnection(
  conn: MetaConnection | GoogleConnection | GSCConnection | GoogleAdsConnection,
  type: 'meta' | 'ga4' | 'gsc' | 'google-ads'
): OAuthConnection {
  const email = type === 'meta'
    ? (conn as MetaConnection).metaUserEmail
    : (conn as GoogleConnection).googleUserEmail
  return {
    id: conn.id,
    type: type as any,
    workspaceId: '',
    status: conn.syncState?.status === 'error'
      ? 'error'
      : conn.tokenExpiresAt && new Date(conn.tokenExpiresAt) < new Date()
        ? 'expired'
        : 'connected',
    connectedEmail: email || '',
    connectedAt: conn.createdAt,
    lastSyncAt: conn.lastSyncAt,
    tokenExpiresAt: conn.tokenExpiresAt,
    metadata: type === 'meta'
      ? { adAccounts: (conn as MetaConnection).adAccounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
      : type === 'ga4'
        ? { ga4Accounts: (conn as GoogleConnection).ga4Accounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
        : type === 'google-ads'
          ? { adAccounts: (conn as GoogleAdsConnection).customerAccounts?.map(a => ({ id: a.id, name: a.name, selected: a.isActive })) }
          : undefined,
  }
}

function formatLastSync(lastSyncAt?: string): string {
  if (!lastSyncAt) return ''
  const diffMs = Date.now() - new Date(lastSyncAt).getTime()
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) return 'Synced just now'
  if (mins < 60) return `Synced ${mins}m ago`
  const hours = Math.floor(diffMs / 3600000)
  if (hours < 24) return `Synced ${hours}h ago`
  return `Synced ${Math.floor(diffMs / 86400000)}d ago`
}

// ── Component ──

export default function Connectors() {
  const ctx = useOutletContext<DashboardContext>()
  const { token } = useAuth()
  const propertyId = ctx?.propertyId || ''
  const siteDomain = ctx?.currentSite?.domain || ctx?.currentDomain || ''

  // Same cache key as WorkspaceSwitcher, so this reuses the sidebar's fetch.
  const { data: workspaces = [] } = useQuery<Workspace[]>({
    queryKey: ['workspaces', token],
    queryFn: () => (token ? getAllWorkspaces(token) : Promise.resolve([])),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  })
  const workspaceName = workspaces.find(w => w.properties?.some(p => p.propertyId === propertyId))?.name
  const workspaceLabel = workspaceName || 'this workspace'

  // Revenue config
  const [revenueConfig, setRevenueConfig] = useState<RevenueSetup | null>(null)
  const [showRevenueAttribution, setShowRevenueAttribution] = useState(true)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)

  // OAuth connections
  const [oauthConnections, setOauthConnections] = useState<OAuthConnection[]>([])
  const [isLoadingConnections, setIsLoadingConnections] = useState(true)

  // Sheet state
  const [sheetType, setSheetType] = useState<SheetConnectorType | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  // Get connection by type
  const getConnection = (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') =>
    oauthConnections.find(c => c.type === type)

  // ── Data Loading ──

  const loadRevenueConfig = useCallback(async () => {
    if (!propertyId || !token) return
    setIsLoadingConfig(true)
    try {
      const response = await getRevenueConfig(propertyId, token)
      const setup = response?.revenueSetup
        ?? ((response?.stripe !== undefined || response?.foxy !== undefined) ? response : null)
      setRevenueConfig(setup)
      setShowRevenueAttribution(response?.showRevenueAttribution ?? true)
    } catch (error) {
      reportConnectorFailure('load_connections', error, { propertyId }, {
        connectorId: 'revenue-config',
      })
    } finally {
      setIsLoadingConfig(false)
    }
  }, [propertyId, token])

  const loadOAuthConnections = useCallback(async () => {
    if (!propertyId || !token) {
      setIsLoadingConnections(false)
      return
    }
    setIsLoadingConnections(true)
    try {
      const listUnavailable = (connectorId: string) => (error: unknown) => {
        logConnectorDegradation('connections_unavailable', { propertyId }, {
          connectorId,
          reason: error instanceof Error ? error.message : 'unknown',
        })
        return []
      }
      const [metaConns, googleConns, gscConns, googleAdsConns] = await Promise.all([
        getMetaConnections(propertyId, token).catch(listUnavailable('meta')) as Promise<MetaConnection[]>,
        getGoogleConnections(propertyId, token).catch(listUnavailable('ga4')) as Promise<GoogleConnection[]>,
        getGSCConnections(propertyId, token).catch(listUnavailable('gsc')) as Promise<GSCConnection[]>,
        getGoogleAdsConnections(propertyId, token).catch(listUnavailable('google-ads')) as Promise<GoogleAdsConnection[]>,
      ])
      setOauthConnections([
        ...metaConns.map(c => toOAuthConnection(c, 'meta')),
        ...googleConns.map(c => toOAuthConnection(c, 'ga4')),
        ...gscConns.map(c => toOAuthConnection(c, 'gsc')),
        ...googleAdsConns.map(c => toOAuthConnection(c, 'google-ads')),
      ])
    } catch (error) {
      reportConnectorFailure('load_connections', error, { propertyId })
      toast.error('Failed to load connections')
    } finally {
      setIsLoadingConnections(false)
    }
  }, [propertyId, token])

  useEffect(() => { loadRevenueConfig() }, [loadRevenueConfig])
  useEffect(() => { loadOAuthConnections() }, [loadOAuthConnections])

  // ── OAuth Handlers ──

  const handleOAuthConnect = async (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') => {
    if (!propertyId || !token) { toast.error('No property selected'); return }
    try {
      const providerName = type === 'ga4' ? 'Google' : type === 'gsc' ? 'Google Search Console' : type === 'google-ads' ? 'Google Ads' : 'Meta'
      toast.loading(`Opening ${providerName} OAuth...`, { id: `${type}-oauth` })
      let authUrl: string
      if (type === 'ga4') authUrl = (await initiateGoogleOAuth(propertyId, token)).authUrl
      else if (type === 'gsc') authUrl = (await initiateGSCOAuth(propertyId, token)).authUrl
      else if (type === 'google-ads') authUrl = (await initiateGoogleAdsOAuth(propertyId, token)).authUrl
      else authUrl = (await initiateMetaOAuth(propertyId, token)).authUrl
      sessionStorage.setItem('oauth_return_path', window.location.pathname)
      logConnectorEvent('oauth_started', { propertyId }, { connectorId: type })
      window.location.href = authUrl
    } catch (error) {
      reportConnectorFailure('oauth_start', error, { propertyId }, { connectorId: type })
      toast.error('Failed to start OAuth flow', {
        description: error instanceof Error ? error.message : 'Unknown error',
        id: `${type}-oauth`,
      })
    }
  }

  const handleOAuthReconnect = async (type: 'ga4' | 'meta' | 'gsc' | 'google-ads') => {
    const connection = getConnection(type)
    if (!connection) return
    try {
      toast.loading('Refreshing token...', { id: 'refresh-token' })
      if (type === 'ga4' || type === 'gsc') await refreshGoogleConnection(connection.id, token!)
      else if (type === 'google-ads') await refreshGoogleAdsConnection(connection.id, token!)
      else await refreshConnection(connection.id, token!)
      logConnectorEvent('connector_connected', { propertyId }, {
        connectorId: type,
        connectionId: connection.id,
      })
      toast.success('Token refreshed', { id: 'refresh-token' })
      await loadOAuthConnections()
    } catch (error) {
      logConnectorDegradation('connection_unhealthy', { propertyId }, {
        connectorId: type,
        connectionId: connection.id,
        reason: error instanceof Error ? error.message : 'refresh_failed',
      })
      toast.error('Refresh failed, redirecting to OAuth...', { id: 'refresh-token' })
      setTimeout(() => handleOAuthConnect(type), 1000)
    }
  }

  const handleOAuthDisconnect = async (connection: OAuthConnection) => {
    try {
      toast.loading('Disconnecting...', { id: 'disconnect' })
      if (connection.type === 'ga4') await disconnectGoogleConnection(connection.id, token!)
      else if (connection.type === 'gsc') await disconnectGSCConnection(connection.id, token!)
      else if ((connection.type as string) === 'google-ads') await disconnectGoogleAdsConnection(connection.id, token!)
      else await disconnectMetaConnection(connection.id, token!)
      logConnectorEvent('connector_disconnected', { propertyId }, {
        connectorId: connection.type,
        connectionId: connection.id,
      })
      toast.success('Disconnected', { id: 'disconnect' })
      await loadOAuthConnections()
    } catch (error) {
      reportConnectorFailure('disconnect', error, { propertyId }, {
        connectorId: connection.type,
        connectionId: connection.id,
      })
      toast.error('Failed to disconnect', {
        description: error instanceof Error ? error.message : 'Unknown error',
        id: 'disconnect',
      })
    }
  }

  const handleOAuthSync = (connection: OAuthConnection) => {
    const label = connection.type === 'ga4' ? 'GA4' : connection.type === 'meta' ? 'Meta Ads' : (connection.type as string) === 'google-ads' ? 'Google Ads' : connection.type
    toast.success('Sync started', {
      description: `Syncing data from ${label}...`,
    })
  }

  // ── Sheet Helpers ──

  const openSheet = (type: SheetConnectorType) => {
    setSheetType(type)
    setSheetOpen(true)
  }

  // ── Derived Status ──

  const stripeConnected = !!revenueConfig?.stripe?.configured
  const foxyConnected = !!revenueConfig?.foxy?.configured
  const metaConn = getConnection('meta')
  const ga4Conn = getConnection('ga4')
  const gscConn = getConnection('gsc')
  const googleAdsConn = getConnection('google-ads')
  const isLoading = isLoadingConfig || isLoadingConnections

  // ── OAuth subtitle builder ──
  const oauthSubtitle = (conn?: OAuthConnection | null) => {
    if (!conn) return undefined
    const parts: string[] = []
    if (conn.connectedEmail) parts.push(conn.connectedEmail)
    if (conn.metadata?.adAccounts) {
      const selected = conn.metadata.adAccounts.filter(a => a.selected).length
      if (selected) parts.push(`${selected} account${selected > 1 ? 's' : ''}`)
    }
    if (conn.lastSyncAt) parts.push(formatLastSync(conn.lastSyncAt))
    return parts.join(' · ') || undefined
  }

  return (
    <PageShell>
      <PageHeader
        title="Connectors"
        description="Where your traffic, ad spend, revenue and form data come from."
        className="mb-7"
      />

      <div className="flex flex-col gap-7">
        <ConnectorSection title="Website traffic" meta="per site">
          <DataSourceRow
            name="Humblytics tracker"
            description="Powers analytics, heatmaps, funnels and A/B tests"
            brand="humblytics"
            status="active"
            statusLabel="Installed"
            subtitle={siteDomain ? `Powers analytics, heatmaps, funnels and A/B tests on ${siteDomain}` : undefined}
            actionLabel="Install guide"
            onClick={() => openSheet('humblytics-script')}
          />
        </ConnectorSection>

        {/* Advertising / Revenue / Forms — driven from the connectors config (single source of truth) */}
        <ConnectorCategorySections
          isLoading={isLoading}
          workspaceName={workspaceName}
          resolver={(id) => {
            switch (id) {
              case 'meta':
                return {
                  status: metaConn
                    ? (metaConn.status === 'connected' ? 'connected' : metaConn.status === 'expired' ? 'expired' : 'error')
                    : 'disconnected',
                  subtitle: oauthSubtitle(metaConn),
                  onClick: () => openSheet('meta'),
                  ...(metaConn?.status === 'expired' && {
                    onSecondaryAction: () => handleOAuthReconnect('meta'),
                    secondaryActionLabel: 'Reconnect',
                  }),
                }
              case 'google-ads':
                if (!googleAdsConn) return { status: 'disconnected', onClick: () => openSheet('google-ads') }
                return {
                  status: googleAdsConn.status === 'connected' ? 'connected' : googleAdsConn.status === 'expired' ? 'expired' : 'error',
                  subtitle: oauthSubtitle(googleAdsConn),
                  onClick: () => openSheet('google-ads'),
                  ...(googleAdsConn.status === 'expired' && {
                    onSecondaryAction: () => handleOAuthReconnect('google-ads'),
                    secondaryActionLabel: 'Reconnect',
                  }),
                }
              case 'stripe':
                return {
                  status: stripeConnected ? 'connected' : 'disconnected',
                  subtitle: stripeConnected ? `All sites in ${workspaceLabel}` : undefined,
                  onClick: () => openSheet('stripe'),
                }
              case 'foxy':
                return {
                  status: foxyConnected ? 'connected' : 'disconnected',
                  subtitle: foxyConnected ? `All sites in ${workspaceLabel}` : undefined,
                  onClick: () => openSheet('foxy'),
                }
              case 'jotform':
                return { status: 'webhook', onClick: () => openSheet('jotform') }
              case 'tally':
                return { status: 'webhook', onClick: () => openSheet('tally') }
              default:
                return null
            }
          }}
        />
      </div>

      {/* ── Connector Sheet ── */}
      <ConnectorSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        connectorType={sheetType}
        propertyId={propertyId}
        token={token!}
        siteDomain={siteDomain}
        workspaceName={workspaceName}
        oauthConnection={
          sheetType === 'meta' ? metaConn
            : sheetType === 'ga4' ? ga4Conn
              : sheetType === 'gsc' ? gscConn
                : (sheetType as string) === 'google-ads' ? googleAdsConn
                  : undefined
        }
        onOAuthConnect={handleOAuthConnect}
        onOAuthReconnect={handleOAuthReconnect}
        onOAuthDisconnect={handleOAuthDisconnect}
        onOAuthSync={handleOAuthSync}
        onConfigChanged={() => {
          loadRevenueConfig()
          loadOAuthConnections()
        }}
        showRevenueAttribution={showRevenueAttribution}
      />
    </PageShell>
  )
}
